/**
 * Command Handlers - Mutate state directly
 * 
 * Convention-based registry pattern:
 * - Each command type has a handler function
 * - Handlers mutate state maps directly
 * - Handlers use shared mutations (same as events)
 * - TypeScript ensures exhaustive coverage
 * - No circular dependencies - handlers don't import memory-store
 */

import * as mutations from '../state/mutations'
import { logger } from '../utils/logger'
import type { Item, List, Scenario, ScenarioItem, Flow } from '../state/mutations'
import type {
  Command,
  CompleteItemCommand,
  RenameItemCommand,
  ReorderItemCommand,
  CreateItemCommand,
  CreateItemsCommand,
  UpdateItemQuantityCommand,
  DeleteItemCommand,
  CreateListCommand,
  RenameListCommand,
  DeleteListCommand,
  CreateFlowCommand,
  RenameFlowCommand,
  DeleteFlowCommand,
  UpdateFlowRankingsCommand,
  SetDefaultFlowCommand,
  SetItemFlowPreferencesCommand,
  CreateScenarioCommand,
  RenameScenarioCommand,
  DeleteScenarioCommand,
  LeaveScenarioCommand,
  DeleteScenarioItemsCommand,
  AddScenarioItemsCommand
} from './types'

/**
 * Memory state interface - passed to handlers for mutations
 */
export interface MemoryState {
  scenarios: Map<string, Scenario>
  lists: Map<string, List>  // Lists contain their items embedded in list.items dictionary
  flows: Map<string, Flow>
  // NOTE: No separate 'items' Map - items are embedded in their parent lists
  scenarioItems: Map<string, ScenarioItem[]>
}

/**
 * Command handler signature - mutates state directly
 */
export type CommandHandler<T extends Command = Command> = (
  command: T,
  state: MemoryState
) => Promise<void>

/**
 * State accessor interface - allows handlers to read current state
 * without importing memory-store (breaks circular dependency)
 */
export interface StateAccessor {
  getItem(listId: string, itemId: string): Item | undefined
  getList(listId: string): List | undefined
  getScenario(scenarioId: string): Scenario | undefined
  getItemsByList(listId: string): Item[]
}

/**
 * Helper: Get item from embedded list.items dictionary
 */
function getItemFromList(state: MemoryState, listId: string, itemId: string): Item | undefined {
  const list = state.lists.get(listId)
  return list?.items?.[itemId]
}

/**
 * Helper: Set item in embedded list.items dictionary
 */
function setItemInList(state: MemoryState, listId: string, item: Item): void {
  const list = state.lists.get(listId)
  if (!list) return
  
  state.lists.set(listId, {
    ...list,
    items: {
      ...list.items,
      [item.id]: item
    }
  })
}

/**
 * Helper: Delete item from embedded list.items dictionary
 */
function deleteItemFromList(state: MemoryState, listId: string, itemId: string): void {
  const list = state.lists.get(listId)
  if (!list?.items?.[itemId]) return
  
  const { [itemId]: _, ...remainingItems } = list.items
  state.lists.set(listId, {
    ...list,
    items: remainingItems
  })
}

// ============================================================================
// Item Command Handlers
// ============================================================================

export async function handleCompleteItem(
  command: CompleteItemCommand,
  state: MemoryState
): Promise<void> {
  const item = getItemFromList(state, command.listId, command.itemId)
  if (!item) return
  
  const updatedItem = mutations.applyItemCompletion(item, command.isComplete)
  setItemInList(state, command.listId, updatedItem)
}

export async function handleRenameItem(
  command: RenameItemCommand,
  state: MemoryState
): Promise<void> {
  const item = getItemFromList(state, command.listId, command.itemId)
  if (item) {
    const updatedItem = mutations.applyItemRename(item, command.newName)
    setItemInList(state, command.listId, updatedItem)
  }

  // Always update scenario items (item may exist only at scenario level)
  const currentItems = state.scenarioItems.get(command.scenarioId) || []
  const updatedItems = mutations.applyScenarioItemRename(currentItems, {
    itemId: command.itemId,
    newName: command.newName
  })
  state.scenarioItems.set(command.scenarioId, updatedItems)
}

export async function handleReorderItem(
    command: ReorderItemCommand,
    state: MemoryState
  ): Promise<void> {
    const item = getItemFromList(state, command.listId, command.itemId)
    if (!item) return
    
    const updatedItem = mutations.applyItemReorder(item, command.newOrder)
    setItemInList(state, command.listId, updatedItem)
  }

export async function handleCreateItem(
  command: CreateItemCommand,
  state: MemoryState
): Promise<void> {
  const newItem = mutations.createItem({
    itemId: command.itemId,
    listId: command.listId,
    scenarioId: command.scenarioId,
    itemName: command.itemName,
    quantity: command.quantity,
    sortOrder: command.order
  })
  setItemInList(state, command.listId, newItem)
  
  // Optimistically update scenario items
  const currentItems = state.scenarioItems.get(command.scenarioId) || []
  const updatedItems = mutations.applyScenarioItemUsage(currentItems, {
    itemId: command.itemId,
    itemName: command.itemName,
    lastUsedAt: new Date().toISOString(),
    quantity: command.quantity
  })
  state.scenarioItems.set(command.scenarioId, updatedItems)
}

export async function handleCreateItems(
  command: CreateItemsCommand,
  state: MemoryState
): Promise<void> {
  const newItems = mutations.createItems({
    items: command.items,
    listId: command.listId,
    scenarioId: command.scenarioId
  })
  
  // Add all items to the list's embedded items dictionary
  for (const item of newItems) {
    setItemInList(state, command.listId, item)
  }
  
  // Optimistically update scenario items for all items
  const currentItems = state.scenarioItems.get(command.scenarioId) || []
  let updatedItems = currentItems
  for (const item of command.items) {
    updatedItems = mutations.applyScenarioItemUsage(updatedItems, {
      itemId: item.itemId,
      itemName: item.itemName,
      lastUsedAt: new Date().toISOString(),
      quantity: item.quantity
    })
  }
  state.scenarioItems.set(command.scenarioId, updatedItems)
}

export async function handleUpdateItemQuantity(
  command: UpdateItemQuantityCommand,
  state: MemoryState
): Promise<void> {
  const item = getItemFromList(state, command.listId, command.itemId)
  if (!item) return

  const updatedItem = mutations.applyItemQuantity(item, command.quantity)
  setItemInList(state, command.listId, updatedItem)

  if (command.updateMetaItem) {
    const currentItems = state.scenarioItems.get(command.scenarioId) || []
    const updatedItems = mutations.applyScenarioItemQuantity(currentItems, {
      itemId: command.itemId,
      quantity: command.quantity
    })
    state.scenarioItems.set(command.scenarioId, updatedItems)
  }
}

export async function handleDeleteItem(
  command: DeleteItemCommand,
  state: MemoryState
): Promise<void> {
  deleteItemFromList(state, command.listId, command.itemId)
}

export async function handleDeleteScenarioItems(
  command: DeleteScenarioItemsCommand,
  state: MemoryState
): Promise<void> {
  const currentItems = state.scenarioItems.get(command.scenarioId) || []
  const updated = mutations.applyDeleteScenarioItems(currentItems, command.itemIds)
  state.scenarioItems.set(command.scenarioId, updated)
}

export async function handleAddScenarioItems(
  command: AddScenarioItemsCommand,
  state: MemoryState
): Promise<void> {
  // ScenarioItem ids are required; they are assigned by the server.
  // The subsequent ScenarioItemsAdded event will update local state.
  void command
  void state
}

// ============================================================================
// List Command Handlers
// ============================================================================

export async function handleCreateList(
  command: CreateListCommand,
  state: MemoryState
): Promise<void> {
  const newList = mutations.createList({
    listId: command.listId,
    scenarioId: command.scenarioId,
    listName: command.listName,
    description: command.description
  })
  state.lists.set(newList.id, newList)
}

export async function handleRenameList(
  command: RenameListCommand,
  state: MemoryState
): Promise<void> {
  const list = state.lists.get(command.listId)
  if (!list) return
  
  const updatedList = mutations.applyListRename(list, command.newName, command.newDescription)
  state.lists.set(updatedList.id, updatedList)
}

export async function handleDeleteList(
  command: DeleteListCommand,
  state: MemoryState
): Promise<void> {
  // Delete list - items are embedded in the list, so they're deleted with it
  state.lists.delete(command.listId)
}

// ============================================================================
// Flow Command Handlers
// ============================================================================

export async function handleCreateFlow(
  command: CreateFlowCommand,
  state: MemoryState
): Promise<void> {
  const newFlow = mutations.createFlow({
    flowId: command.flowId,
    scenarioId: command.scenarioId,
    flowName: command.flowName,
    description: command.description,
    color: command.color
  })
  state.flows.set(newFlow.id, newFlow)
}

export async function handleRenameFlow(
  command: RenameFlowCommand,
  state: MemoryState
): Promise<void> {
  const flow = state.flows.get(command.flowId)
  if (!flow) return
  
  const updatedFlow = mutations.applyFlowRename(
    flow, 
    command.newName, 
    command.newDescription,
    command.newColor
  )
  state.flows.set(updatedFlow.id, updatedFlow)
}

export async function handleDeleteFlow(
  command: DeleteFlowCommand,
  state: MemoryState
): Promise<void> {
  state.flows.delete(command.flowId)
}

export async function handleUpdateFlowRankings(
  command: UpdateFlowRankingsCommand,
  state: MemoryState
): Promise<void> {
  const flow = state.flows.get(command.flowId)
  if (!flow) return
  
  const updatedFlow = mutations.applyFlowRankingsUpdate(flow, command.rankings)
  state.flows.set(updatedFlow.id, updatedFlow)
}

export async function handleSetItemFlowPreferences(
  command: SetItemFlowPreferencesCommand,
  state: MemoryState
): Promise<void> {
  for (const [flowId, pref] of Object.entries(command.flowPreferences)) {
    const flow = state.flows.get(flowId)
    if (!flow) continue

    const existingRanking = flow.rankings[command.itemId]
    const updatedRanking = {
      r: existingRanking?.r ?? 0,
      n: existingRanking?.n ?? '',
      notAvailable: pref.status === 'notAvailable',
      status: pref.status
    }
    const updatedFlow = mutations.applyFlowRankingsUpdate(flow, { [command.itemId]: updatedRanking })
    state.flows.set(updatedFlow.id, updatedFlow)
  }
}

export async function handleSetDefaultFlow(
  command: SetDefaultFlowCommand,
  state: MemoryState
): Promise<void> {
  const scenario = state.scenarios.get(command.scenarioId)
  if (scenario) {
    const updatedScenario = { ...scenario, defaultFlowId: command.flowId }
    state.scenarios.set(updatedScenario.id, updatedScenario)
  }
}

// ============================================================================
// Scenario Command Handlers
// ============================================================================

export async function handleCreateScenario(
  _command: CreateScenarioCommand,
  _state: MemoryState
): Promise<void> {
  // CreateScenario: Scenario already exists in IndexedDB (saved by useCommand.createScenario)
  // Loading handled by memory-store.applyCommand() before calling this handler
  logger.debug(`[CommandHandler] CreateScenario - no-op (already loaded)`)
}

export async function handleRenameScenario(
  command: RenameScenarioCommand,
  state: MemoryState
): Promise<void> {
  const scenario = state.scenarios.get(command.scenarioId)
  if (!scenario) return
  
  const updatedScenario = mutations.applyScenarioRename(scenario, command.newName, command.newDescription)
  state.scenarios.set(updatedScenario.id, updatedScenario)
}

export async function handleDeleteScenario(
  command: DeleteScenarioCommand,
  state: MemoryState
): Promise<void> {
  const scenario = state.scenarios.get(command.scenarioId)
  if (!scenario) return
  
  const deletedScenario = mutations.applyScenarioDeletion(scenario)
  state.scenarios.set(deletedScenario.id, deletedScenario)
}

async function handleLeaveScenario(
  command: LeaveScenarioCommand,
  state: MemoryState
): Promise<void> {
  // When leaving, we just remove the scenario from local state
  state.scenarios.delete(command.scenarioId)
}

// ============================================================================
// Type-Safe Command Handler Registry
// ============================================================================

/**
 * Handler registry - TypeScript enforces exhaustive command coverage!
 * Each command type must have a handler
 */
type CommandHandlerMap = {
  [K in Command['commandType']]: CommandHandler<Extract<Command, { commandType: K }>>
}

export const commandHandlerRegistry: CommandHandlerMap = {
  CompleteItem: handleCompleteItem,
  RenameItem: handleRenameItem,
  ReorderItem: handleReorderItem,
  CreateItem: handleCreateItem,
  CreateItems: handleCreateItems,
  UpdateItemQuantity: handleUpdateItemQuantity,
  DeleteItem: handleDeleteItem,
  CreateList: handleCreateList,
  RenameList: handleRenameList,
  DeleteList: handleDeleteList,
  CreateFlow: handleCreateFlow,
  RenameFlow: handleRenameFlow,
  DeleteFlow: handleDeleteFlow,
  UpdateFlowRankings: handleUpdateFlowRankings,
  SetDefaultFlow: handleSetDefaultFlow,
  SetItemFlowPreferences: handleSetItemFlowPreferences,
  CreateScenario: handleCreateScenario,
  RenameScenario: handleRenameScenario,
  DeleteScenario: handleDeleteScenario,
  LeaveScenario: handleLeaveScenario,
  DeleteScenarioItems: handleDeleteScenarioItems,
  AddScenarioItems: handleAddScenarioItems
}

/**
 * Get handler for a command type
 * TypeScript ensures the handler exists and has correct type
 */
export function getHandlerForCommand(commandType: Command['commandType']): CommandHandler<Command> {
  return commandHandlerRegistry[commandType] as CommandHandler<Command>
}
